import { AxiosError } from 'axios';
import { useSelector } from 'react-redux';
import { mapErrorToMaritalKey } from '../../pages/account-info/features/marital-status/marital-status-helpers';
import { FormTypes } from '../../pages/account-info/models/form-models';
import { mapErrorToNewHireNoneStipKey, mapErrorToNewHireStipKey } from '../../pages/arc/features/new-hire/new-hire-request';
import { mapErrorToPensionEnrollmentKey } from '../../pages/arc/features/pension-enrollment/pension-enrollment-form-helpers';
import { mapErrorToTerminationKey } from '../../pages/arc/features/termination/employment-termination-helpers';
import { TcsmStates } from '../../root/tcsm-model';
import { BaseAppState } from '../models/redux-state';
import { GetErrorFromPlan } from '../../pages/arc/features/beneficiary/helper/beneficiary-helper';
import { GetErrorFromAdoptPlan, mapErrorToPlanAdoptionKey } from '../../pages/arc/features/institutions/plan-adoption/models/plan-adoption-requests';

interface ErrorItem {
	code: string,
	message: string,
}

const getMappedApiMessage = (error: ErrorItem, messageMapping: { [key: string]: string }) => {
	if (error?.code) {
		return messageMapping[error.code] ? messageMapping[error.code] : error?.message;
	}
	return error;
};

const mapFormKey = (errorKey: string, formType: FormTypes) => {
	switch (formType) {
		case FormTypes.MARITAL_STATUS:
			return mapErrorToMaritalKey(errorKey);
		case FormTypes.PENSION_ENROLLMENT:
			return mapErrorToPensionEnrollmentKey(errorKey);
		case FormTypes.TERMINATION:
			return mapErrorToTerminationKey(errorKey);
		case FormTypes.STIP_NEW_HIRE:
			return mapErrorToNewHireStipKey(errorKey);
		case FormTypes.NON_STIP_NEW_HIRE:
			return mapErrorToNewHireNoneStipKey(errorKey);
		case FormTypes.ADOPT_INSTITUTION_PLAN_LAY_DC:
		case FormTypes.ADOPT_INSTITUTION_PLAN_RSVP:
			return mapErrorToPlanAdoptionKey(errorKey);
		// add more form types here if any need specific mappings

		default:
			return errorKey;
	}
};

const getResponseErrors = (error: AxiosError<any>, formType?: FormTypes) => {
	try {		
		const defaultErrors = error?.response?.data?.data?.errors
			|| (error?.response?.data?.data && error.response?.data?.data[0]?.errors);

		switch (formType) {
			case FormTypes.TERMINATION:
				return error?.response?.data?.data?.errors?.employment || error?.response?.data?.data?.errors?.unpaid_leaves ||
					defaultErrors;
			case FormTypes.STIP_NEW_HIRE:
				return error?.response?.data?.data?.errors?.compensation || error?.response?.data?.data?.errors?.employment || error?.response?.data?.data?.errors?.otp ||
					defaultErrors;
			case FormTypes.NON_STIP_NEW_HIRE:
				return error?.response?.data?.data?.errors?.compensation || error?.response?.data?.data?.errors?.employment ||
					defaultErrors;
			case FormTypes.COMPENSATION:
				return error?.response?.data?.data?.otp[0]?.errors ||
					defaultErrors;
			case FormTypes.BENEFIT_SURVEY:
				return error?.response?.data?.errors?.VALIDATION || defaultErrors;
			case FormTypes.NEWHIRE_PENSION_ENROLLMENT:
				return error?.response?.data?.data?.map(a=> a?.errors?.base[0]?.code) || defaultErrors;
			case FormTypes.BENEFICIARY_CREATE:
				return error?.response?.data?.data?.errors; 
			case FormTypes.BENEFICIARY_UPDATE_CPF_LIFE:
				return GetErrorFromPlan(error?.response?.data?.data?.cpf_life) || defaultErrors;
			case FormTypes.BENEFICIARY_UPDATE_PRERETIREMENT:
				return GetErrorFromPlan(error?.response?.data?.data?.preretirement) || defaultErrors;
			case FormTypes.BENEFICIARY_UPDATE_LAY_LUMP_SUM:
				return GetErrorFromPlan(error?.response?.data?.data?.lay_lump_sum) || defaultErrors;
			
			case FormTypes.ADOPT_INSTITUTION_PLAN_LAY_DC:
			case FormTypes.ADOPT_INSTITUTION_PLAN_RSVP:
				return GetErrorFromAdoptPlan(error?.response?.data?.data?.plan || error?.response?.data?.data?.amendment) || defaultErrors;

			case FormTypes.ADOPT_INSTITUTION_PLAN_LAY_DB: {
				const dbPlanErrors = error?.response?.data?.data?.plan?.errors?.plan;
				return (dbPlanErrors ? GetErrorFromAdoptPlan({ errors: dbPlanErrors }) : null) || defaultErrors;
			}
				
			default:
				return defaultErrors;
		}
	} catch {
		return null;
	}
};

export function useApiResponseError(formType?: FormTypes) {
	const error = useSelector<BaseAppState, AxiosError[]>(state => state.apiState?.error);
	const messageMapping = useSelector<TcsmStates, { [key: string]: string }>(a => a.epiProps?.siteSettingsState?.siteSettings?.apiResponseErrorMessages);
	const errors = {};
	
	if (error && error.length > 0) {		
		const responseErrors = getResponseErrors(error[0], formType);
		
		if (responseErrors) {
			const keys = Object.keys(responseErrors);
			
			for (let i = 0; i < keys.length; i += 1) {
				const indexedError = responseErrors[keys[i]];

				const trimmedMappedKey = mapFormKey(keys[i].trim().toLowerCase(), formType);
				let hasInnerMessages = false;

				if (!indexedError?.code && !Array.isArray(indexedError)){
					const innerKeys = Object.keys(indexedError);					
					if (innerKeys?.length > 0){
						for (let j = 0; j < innerKeys.length; j += 1) {
							const trimmedinnerMappedKey = mapFormKey(innerKeys[j].trim().toLowerCase(), formType);
							const innerItem = indexedError[innerKeys[j]];

							if (Array.isArray(innerItem) && innerItem.length > 0 && innerItem[0].code){									
								const message = innerItem.map(a => getMappedApiMessage(a, messageMapping))?.join(', ')?.trim();
								if (message){
									errors[trimmedinnerMappedKey] = message;
									hasInnerMessages = true;
								}
							}
						}						
					}
				}

				if (!hasInnerMessages) {	
					const mappedError = Array.isArray(indexedError) ? responseErrors[keys[i]].map(a => getMappedApiMessage(a, messageMapping)).join(', ') : getMappedApiMessage(indexedError, messageMapping);
					
					if (typeof mappedError === 'string' && mappedError){
						errors[trimmedMappedKey] = mappedError;
					}
				}				
			}
		}
	}

	return errors;
}
